import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useFormStoreSelector } from "../hooks/use-form-store.js";
import { useSchemaContext } from "../hooks/use-schema-context.js";
import { resolveRef, resolveSchemaAtPath } from "../utils/schema-context.js";
// biome-ignore lint/nursery/noImportCycles: Intentional cycle for recursive form rendering (Field -> ShapeField -> ObjectField -> ShapeField)
import { ShapeField } from "./shape-field.js";
/**
 * Renders a single form field by path, resolving schema and config automatically
 * from the form store. Use this when you need to place individual fields in a
 * custom layout rather than using ObjectField's automatic enumeration.
 *
 * @example
 * <ShapeForm store={store} widgets={widgets}>
 *   <div className="grid grid-cols-2 gap-4">
 *     <Field path="name" />
 *     <Field path="description" />
 *   </div>
 *   <Field path="execution.query" />
 * </ShapeForm>
 */
export function Field({ path, config, className }) {
    const ctx = useSchemaContext();
    const rootSchema = useFormStoreSelector((s) => s.schema);
    const resolved = useMemo(() => {
        const segments = path.split('.');
        const fieldName = segments[segments.length - 1];
        const parentPath = segments.slice(0, -1).join('.');
        // Resolve the parent schema to determine if this field is required
        const parentSchema = parentPath ? resolveSchemaAtPath(ctx, parentPath) : rootSchema;
        const resolvedParent = parentSchema ? resolveRef(ctx, parentSchema) : undefined;
        // Resolve this field's schema
        const fieldSchema = resolveSchemaAtPath(ctx, path);
        if (!fieldSchema)
            return null;
        return {
            schema: fieldSchema,
            parentSchema: resolvedParent,
            fieldName
        };
    }, [ctx, rootSchema, path]);
    if (!resolved) {
        console.warn(`Field: Could not resolve schema for path "${path}"`);
        return null;
    }
    const content = (_jsx(ShapeField, { path: path, schema: resolved.schema, parentSchema: resolved.parentSchema, fieldName: resolved.fieldName, config: config }));
    if (className) {
        return _jsx("div", { className: className, children: content });
    }
    return content;
}
